import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { client, Resource } from 'api';

export type EmailIntegration = {
  id: number;
  email: string;
};

export type ConnectGmailParams = {
  authCode: string;
  targetType: string;
};

export type ReconnectGmailParams = {
  integrationId: number;
  authCode: string;
};

export const useEmailIntegrationsQuery = () => {
  return useQuery<EmailIntegration[], AxiosError>(
    [Resource.EmailIntegrations],
    () =>
      client.get(Resource.EmailIntegrations).then(({ data }) => data.results),
    {
      refetchOnWindowFocus: false,
    }
  );
};

export const useDeleteEmailIntegrationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, number>(
    (integrationId: number) =>
      client
        .delete(`${Resource.EmailIntegrations}/${integrationId}`)
        .then(({ data }) => data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([Resource.EmailIntegrations]);
      },
    }
  );
};

export const useConnectGmailMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<EmailIntegration, AxiosError, string>(
    (authCode: string) =>
      client
        .post(Resource.EmailIntegrationsGmail, {
          authCode,
          targetType: 'account', // TODO: Should also take "group" once implemented on the backend
        })
        .then(({ data }) => data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([Resource.EmailIntegrations]);
      },
    }
  );
};

export const useReconnectGmailMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<EmailIntegration, AxiosError, ReconnectGmailParams>(
    ({ integrationId, authCode }: ReconnectGmailParams) =>
      client
        .patch(`${Resource.EmailIntegrations}/${integrationId}`, { authCode })
        .then(({ data }) => data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([Resource.EmailIntegrations]);
      },
    }
  );
};
